import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gameAPI, registrationAPI, drinksAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './GameDetail.css';

const GameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationType, setRegistrationType] = useState('single');
  const [registrationData, setRegistrationData] = useState({
    // Einzelanmeldung
    ticketType: 'stehplatz',
    drinks: [],
    // Gruppenanmeldung
    participantCount: 1,
    participants: [{
      firstName: '',
      lastName: '',
      ticketType: 'stehplatz',
      drinks: [],
      // Champions League Felder
      address: '',
      idCardNumber: '',
      dateOfBirth: '',
      placeOfBirth: ''
    }],
    message: ''
  });
  const [isRegistered, setIsRegistered] = useState(false);
  const [userRegistration, setUserRegistration] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [drinkTypes, setDrinkTypes] = useState([]);

  const ticketTypes = [
    { value: 'stehplatz', label: 'Stehplatz' },
    { value: 'sitzplatz', label: 'Sitzplatz' },
    { value: 'ohne-ticket', label: 'Ohne Ticket (nur Anfahrt)' }
  ];

  useEffect(() => {
    loadGameDetails();
    loadDrinks();
  }, [id]);

  useEffect(() => {
    if (user && game) {
      checkUserRegistration();
    }
  }, [user, game]);

  const loadDrinks = async () => {
    try {
      const response = await drinksAPI.getAllDrinks();
      if (response.success && response.drinks) {
        setDrinkTypes(response.drinks.map(drink => ({
          value: drink.value,
          label: drink.name
        })));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Getränke:', error);
      // Fallback auf Standard-Getränke
      setDrinkTypes([
        { value: 'bier', label: 'Bier' },
        { value: 'aeppler', label: 'Äppler' },
        { value: 'cola', label: 'Cola' }
      ]);
    }
  };

  const loadGameDetails = async () => {
    try {
      const response = await gameAPI.getGame(id);
      if (response.success) {
        setGame(response.game);
      } else {
        toast.error('Spiel nicht gefunden');
        navigate('/spiele');
      }
    } catch (error) {
      console.error('Fehler beim Laden des Spiels:', error);
      toast.error('Fehler beim Laden des Spiels');
      navigate('/spiele');
    } finally {
      setLoading(false);
    }
  };

  const checkUserRegistration = async () => {
    if (!user) return;

    try {
      const response = await registrationAPI.checkRegistration(id);
      if (response.success && response.registered) {
        setIsRegistered(true);
        setUserRegistration(response.registration);

        // Wenn es eine bestehende Anmeldung gibt, lade die Gruppendaten
        if (response.registration.participants) {
          const participants = response.registration.participants;
          setRegistrationData({
            participantCount: participants.length,
            participants: participants,
            message: response.registration.message || '',
            drink: participants[0]?.drink || 'bier',
            ticketType: participants[0]?.ticketType || 'stehplatz'
          });
          // Setze den Registrierungstyp basierend auf der Anzahl
          setRegistrationType(participants.length > 1 ? 'group' : 'single');
        } else {
          // Fallback für alte Einzelanmeldungen
          const participantData = {
            firstName: user.firstName || user.name?.split(' ')[0] || '',
            lastName: user.lastName || user.name?.split(' ')[1] || '',
            ticketType: response.registration.ticket_type || 'stehplatz',
            drink: response.registration.drink || 'bier'
          };
          setRegistrationData({
            ticketType: response.registration.ticket_type || 'stehplatz',
            drink: response.registration.drink || 'bier',
            participantCount: 1,
            participants: [participantData],
            message: response.registration.message || ''
          });
          setRegistrationType('single');
        }
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der Anmeldung:', error);
    }
  };

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;

    if (name === 'participantCount') {
      const count = parseInt(value);
      const newParticipants = [];

      // Existing participants beibehalten oder neue hinzufügen
      for (let i = 0; i < count; i++) {
        newParticipants.push(
          registrationData.participants[i] ||
          {
            firstName: '',
            lastName: '',
            ticketType: 'stehplatz',
            drinks: [],
            // Champions League Felder
            address: '',
            idCardNumber: '',
            dateOfBirth: '',
            placeOfBirth: ''
          }
        );
      }

      setRegistrationData({
        ...registrationData,
        participantCount: count,
        participants: newParticipants
      });
    } else {
      setRegistrationData({
        ...registrationData,
        [name]: value
      });
    }
  };

  const handleDrinkToggle = (drinkValue) => {
    const currentDrinks = registrationData.drinks || [];
    const newDrinks = currentDrinks.includes(drinkValue)
      ? currentDrinks.filter(d => d !== drinkValue)
      : [...currentDrinks, drinkValue];

    setRegistrationData({
      ...registrationData,
      drinks: newDrinks
    });
  };

  const handleParticipantDrinkToggle = (index, drinkValue) => {
    const updatedParticipants = [...registrationData.participants];
    const currentDrinks = updatedParticipants[index].drinks || [];
    const newDrinks = currentDrinks.includes(drinkValue)
      ? currentDrinks.filter(d => d !== drinkValue)
      : [...currentDrinks, drinkValue];

    updatedParticipants[index] = {
      ...updatedParticipants[index],
      drinks: newDrinks
    };

    setRegistrationData({
      ...registrationData,
      participants: updatedParticipants
    });
  };

  const handleParticipantChange = (index, e) => {
    const { name, value } = e.target;
    const updatedParticipants = [...registrationData.participants];
    updatedParticipants[index] = {
      ...updatedParticipants[index],
      [name]: value
    };

    setRegistrationData({
      ...registrationData,
      participants: updatedParticipants
    });
  };

  const handleRegistration = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Sie müssen angemeldet sein, um sich zu einem Spiel anzumelden.');
      navigate('/login');
      return;
    }

    setRegistrationLoading(true);

    try {
      let response;

      if (registrationType === 'single') {
        // Einzelanmeldung - Namen aus User-Daten extrahieren
        const firstName = user.firstName || user.first_name || user.name?.split(' ')[0] || 'Unbekannt';
        const lastName = user.lastName || user.last_name || user.name?.split(' ').slice(1).join(' ') || 'Unbekannt';

        console.log('=== FRONTEND DEBUG START ===');
        console.log('Einzelanmeldung für:', firstName, lastName);
        console.log('registrationData.ticketType:', registrationData.ticketType);
        console.log('Vollständige registrationData:', registrationData);

        const requestData = {
          gameId: parseInt(id),
          participants: [{
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            ticketType: registrationData.ticketType,
            drinks: registrationData.drinks || [],
            // Champions League Felder (falls vorhanden)
            address: registrationData.address || null,
            idCardNumber: registrationData.idCardNumber || null,
            dateOfBirth: registrationData.dateOfBirth || null,
            placeOfBirth: registrationData.placeOfBirth || null
          }],
          message: registrationData.message || ''
        };

        console.log('=== SENDE ANFRAGE ===');
        console.log('Request Data:', JSON.stringify(requestData, null, 2));
        console.log('participants[0].ticketType:', requestData.participants[0].ticketType);
        console.log('=== FRONTEND DEBUG END ===');
        response = await registrationAPI.register(requestData);
      } else {
        // Gruppenanmeldung
        console.log('Gruppenanmeldung Daten:', registrationData.participants); // Debug

        const invalidParticipant = registrationData.participants.find(
          p => !p.firstName || !p.firstName.trim() || !p.lastName || !p.lastName.trim()
        );

        if (invalidParticipant) {
          console.log('Ungültiger Teilnehmer:', invalidParticipant); // Debug
          toast.error('Bitte füllen Sie alle Teilnehmer-Daten aus.');
          return;
        }

        // Alle Teilnehmer-Namen trimmen
        const cleanedParticipants = registrationData.participants.map(p => ({
          ...p,
          firstName: p.firstName.trim(),
          lastName: p.lastName.trim()
        }));

        response = await registrationAPI.register({
          gameId: parseInt(id),
          participants: cleanedParticipants,
          message: registrationData.message || ''
        });
      }

      if (response.success) {
        const participantCount = registrationType === 'single' ? 1 : registrationData.participants.length;
        toast.success(`${participantCount === 1 ? 'Erfolgreich angemeldet!' : `Gruppe mit ${participantCount} Personen erfolgreich angemeldet!`}`);
        setIsRegistered(true);

        // Spiel-Details neu laden
        await loadGameDetails();
        await checkUserRegistration();
      }
    } catch (error) {
      console.error('Fehler bei der Anmeldung:', error);
      console.error('Error response:', error.response); // Debug

      let errorMessage = 'Fehler bei der Anmeldung';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!window.confirm('Möchten Sie Ihre Anmeldung wirklich stornieren?')) {
      return;
    }

    setRegistrationLoading(true);

    try {
      const response = await registrationAPI.cancelRegistration(parseInt(id));

      if (response.success) {
        setIsRegistered(false);
        setUserRegistration(null);
        setRegistrationData({
          ticketType: 'stehplatz',
          drinks: [],
          participantCount: 1,
          participants: [{ firstName: '', lastName: '', ticketType: 'stehplatz', drinks: [] }],
          message: ''
        });
        setRegistrationType('single');
        setIsEditMode(false);

        toast.success('Ihre Anmeldung wurde storniert.');

        // Spiel-Details neu laden
        await loadGameDetails();
      }
    } catch (error) {
      console.error('Fehler beim Stornieren:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Stornieren';
      toast.error(errorMessage);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleUpdateRegistration = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Sie müssen angemeldet sein.');
      return;
    }

    setRegistrationLoading(true);

    try {
      // Validierung für Gruppenanmeldungen
      if (registrationType === 'group') {
        const invalidParticipant = registrationData.participants.find(
          p => !p.firstName || !p.firstName.trim() || !p.lastName || !p.lastName.trim()
        );

        if (invalidParticipant) {
          toast.error('Bitte füllen Sie alle Teilnehmer-Daten aus.');
          setRegistrationLoading(false);
          return;
        }
      }

      // Alle Teilnehmer-Namen trimmen
      const cleanedParticipants = registrationData.participants.map(p => ({
        ...p,
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim()
      }));

      const response = await registrationAPI.updateRegistration(parseInt(id), {
        gameId: parseInt(id),
        participants: cleanedParticipants,
        message: registrationData.message || ''
      });

      if (response.success) {
        toast.success('Anmeldung wurde erfolgreich aktualisiert!');
        setIsEditMode(false);

        // Spiel-Details neu laden
        await loadGameDetails();
        await checkUserRegistration();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Anmeldung:', error);

      let errorMessage = 'Fehler beim Aktualisieren der Anmeldung';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setRegistrationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <p>Spiel wird geladen...</p>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="container">
        <div className="error">
          <h2>Spiel nicht gefunden</h2>
          <p>Das angeforderte Spiel existiert nicht.</p>
          <button onClick={() => navigate('/spiele')} className="btn btn-primary">
            Zurück zu allen Spielen
          </button>
        </div>
      </div>
    );
  }

  const getTicketTypeLabel = (type) => {
    const ticketType = ticketTypes.find(t => t.value === type);
    return ticketType ? ticketType.label : type;
  };

  const getDrinkLabel = (type) => {
    if (!type) return 'Nicht angegeben';
    const drinkType = drinkTypes.find(t => t.value === type);
    return drinkType ? drinkType.label : type;
  };

  return (
    <div className="game-detail">
      <div className="container">
        <div className="game-detail-header">
          <button onClick={() => navigate('/spiele')} className="back-btn">
            ← Zurück zu allen Spielen
          </button>
          <h1>{game.opponent}</h1>
        </div>

        <div className="game-detail-content single-column">
          <div className="game-info-section">
            <div className="game-main-info card">
              <h2>Spielinformationen</h2>
              <div className="info-grid">
                <div className="info-item">
                  <strong>Datum:</strong> {new Date(game.game_date).toLocaleDateString('de-DE')}
                </div>
                <div className="info-item">
                  <strong>Uhrzeit:</strong> {game.game_time}
                </div>
                <div className="info-item">
                  <strong>Ort:</strong> {game.location}
                </div>
                <div className="info-item">
                  <strong>Kategorie:</strong> {game.category}
                </div>
              </div>

              {game.description && (
                <div className="game-description">
                  <h3>Beschreibung</h3>
                  <p>{game.description}</p>
                </div>
              )}

            </div>

            <div className="registration-section card">
              <h2>Anmeldung</h2>

              {!user ? (
                <div className="login-required">
                  <p>Sie müssen angemeldet sein, um sich zu einem Spiel anzumelden.</p>
                  <button onClick={() => navigate('/login')} className="btn btn-primary">
                    Jetzt anmelden
                  </button>
                </div>
              ) : (
                <>
                  <form onSubmit={isRegistered && isEditMode ? handleUpdateRegistration : handleRegistration}>
                    {isRegistered && !isEditMode ? (
                      // Anzeige der bestehenden Anmeldung
                      <div className="current-registration">
                        <div className="registration-info">
                          <p className="success-message">✅ Sie sind bereits zu diesem Spiel angemeldet!</p>

                          {userRegistration && (
                            <div className="registration-details">
                              <h4>Ihre Anmeldung:</h4>
                              {registrationData.participants && registrationData.participants.length > 0 && (
                                <div className="participants-list">
                                  {registrationData.participants.map((participant, index) => (
                                    <div key={index} className="participant-info">
                                      <strong>Teilnehmer {index + 1}:</strong> {participant.firstName} {participant.lastName}
                                      {' - '}{getTicketTypeLabel(participant.ticketType)}
                                      {' - '}{getDrinkLabel(participant.drink || 'bier')}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {registrationData.message && (
                                <div className="registration-message">
                                  <strong>Nachricht:</strong> {registrationData.message}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="registration-actions">
                          <button
                            type="button"
                            onClick={() => setIsEditMode(true)}
                            className="btn btn-primary"
                            disabled={registrationLoading}
                          >
                            ✏️ Anmeldung bearbeiten
                          </button>
                          <button
                            type="button"
                            onClick={handleUnregister}
                            className="btn btn-danger"
                            disabled={registrationLoading}
                          >
                            {registrationLoading ? 'Wird storniert...' : 'Anmeldung stornieren'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Anmeldungstyp nur anzeigen wenn nicht bereits angemeldet */}
                        {!isRegistered && (
                          <div className="registration-type-selection">
                            <div className="form-group">
                              <label>Anmeldungsart</label>
                              <div className="radio-group">
                                <label className="radio-option">
                                  <input
                                    type="radio"
                                    name="registrationType"
                                    value="single"
                                    checked={registrationType === 'single'}
                                    onChange={(e) => setRegistrationType(e.target.value)}
                                    disabled={registrationLoading}
                                  />
                                  <span>Einzelanmeldung</span>
                                </label>
                                <label className="radio-option">
                                  <input
                                    type="radio"
                                    name="registrationType"
                                    value="group"
                                    checked={registrationType === 'group'}
                                    onChange={(e) => setRegistrationType(e.target.value)}
                                    disabled={registrationLoading}
                                  />
                                  <span>Gruppenanmeldung</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                        {registrationType === 'single' && !isEditMode ? (
                          // Einzelanmeldung Formular
                          <div className="single-registration">
                            <div className="form-group">
                              <label htmlFor="ticketType">Ticket-Art</label>
                              <select
                                id="ticketType"
                                name="ticketType"
                                value={registrationData.ticketType || 'stehplatz'}
                                onChange={handleRegistrationChange}
                                required
                                disabled={registrationLoading}
                              >
                                {ticketTypes.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Ich trinke hauptsächlich... (Mehrfachauswahl möglich)</label>
                              <div className="drinks-checkbox-group">
                                {drinkTypes.map(type => (
                                  <label key={type.value} className="drink-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={(registrationData.drinks || []).includes(type.value)}
                                      onChange={() => handleDrinkToggle(type.value)}
                                      disabled={registrationLoading}
                                    />
                                    <span>{type.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Champions League Zusatzfelder */}
                            {game.category === 'champions-league' && (
                              <div className="cl-fields-info">
                                <p className="info-text">ℹ️ <strong>Hinweis:</strong> Für Champions League Spiele sind zusätzliche Angaben für die Ticketbestellung erforderlich.</p>

                                <div className="form-group">
                                  <label htmlFor="address">Adresse *</label>
                                  <textarea
                                    id="address"
                                    name="address"
                                    value={registrationData.address || ''}
                                    onChange={handleRegistrationChange}
                                    required
                                    disabled={registrationLoading}
                                    placeholder="Straße, Hausnummer, PLZ, Ort"
                                    rows="2"
                                  />
                                </div>

                                <div className="form-row">
                                  <div className="form-group">
                                    <label htmlFor="idCardNumber">Personalausweisnummer *</label>
                                    <input
                                      type="text"
                                      id="idCardNumber"
                                      name="idCardNumber"
                                      value={registrationData.idCardNumber || ''}
                                      onChange={handleRegistrationChange}
                                      required
                                      disabled={registrationLoading}
                                      placeholder="z.B. L01X00T478"
                                    />
                                  </div>

                                  <div className="form-group">
                                    <label htmlFor="dateOfBirth">Geburtsdatum *</label>
                                    <input
                                      type="date"
                                      id="dateOfBirth"
                                      name="dateOfBirth"
                                      value={registrationData.dateOfBirth || ''}
                                      onChange={handleRegistrationChange}
                                      required
                                      disabled={registrationLoading}
                                    />
                                  </div>
                                </div>

                                <div className="form-group">
                                  <label htmlFor="placeOfBirth">Geburtsort *</label>
                                  <input
                                    type="text"
                                    id="placeOfBirth"
                                    name="placeOfBirth"
                                    value={registrationData.placeOfBirth || ''}
                                    onChange={handleRegistrationChange}
                                    required
                                    disabled={registrationLoading}
                                    placeholder="z.B. Frankfurt am Main"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Gruppenanmeldung Formular (auch im Edit-Modus)
                          <div className="group-registration">
                            {isEditMode && (
                              <div className="edit-mode-info">
                                <p>✏️ Bearbeitungsmodus - Sie können Teilnehmer hinzufügen oder ändern</p>
                              </div>
                            )}

                            <div className="form-group">
                              <label htmlFor="participantCount">Anzahl Teilnehmer</label>
                              <select
                                id="participantCount"
                                name="participantCount"
                                value={registrationData.participantCount || 1}
                                onChange={handleRegistrationChange}
                                required
                                disabled={registrationLoading}
                              >
                                {[...Array(50)].map((_, i) => (
                                  <option key={i + 1} value={i + 1}>
                                    {i + 1} {i === 0 ? 'Person' : 'Personen'}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {registrationData.participants && registrationData.participants.map((participant, index) => (
                              <div key={index} className="participant-form">
                                <h4>Teilnehmer {index + 1}</h4>
                                <div className="form-row">
                                  <div className="form-group">
                                    <label htmlFor={`firstName_${index}`}>Vorname</label>
                                    <input
                                      type="text"
                                      id={`firstName_${index}`}
                                      name="firstName"
                                      value={participant.firstName}
                                      onChange={(e) => handleParticipantChange(index, e)}
                                      required
                                      disabled={registrationLoading}
                                      placeholder="Vorname"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`lastName_${index}`}>Nachname</label>
                                    <input
                                      type="text"
                                      id={`lastName_${index}`}
                                      name="lastName"
                                      value={participant.lastName}
                                      onChange={(e) => handleParticipantChange(index, e)}
                                      required
                                      disabled={registrationLoading}
                                      placeholder="Nachname"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`ticketType_${index}`}>Ticket-Art</label>
                                    <select
                                      id={`ticketType_${index}`}
                                      name="ticketType"
                                      value={participant.ticketType}
                                      onChange={(e) => handleParticipantChange(index, e)}
                                      required
                                      disabled={registrationLoading}
                                    >
                                      {ticketTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                          {type.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label>Ich trinke hauptsächlich... (Mehrfachauswahl möglich)</label>
                                    <div className="drinks-checkbox-group">
                                      {drinkTypes.map(type => (
                                        <label key={type.value} className="drink-checkbox-label">
                                          <input
                                            type="checkbox"
                                            checked={(participant.drinks || []).includes(type.value)}
                                            onChange={() => handleParticipantDrinkToggle(index, type.value)}
                                            disabled={registrationLoading}
                                          />
                                          <span>{type.label}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                {/* Champions League Zusatzfelder für jeden Teilnehmer */}
                                {game.category === 'champions-league' && (
                                  <div className="cl-participant-fields">
                                    {index === 0 && (
                                      <p className="info-text">ℹ️ <strong>Hinweis:</strong> Für Champions League Spiele sind zusätzliche Angaben für die Ticketbestellung erforderlich.</p>
                                    )}

                                    <div className="form-group">
                                      <label htmlFor={`address_${index}`}>Adresse *</label>
                                      <textarea
                                        id={`address_${index}`}
                                        name="address"
                                        value={participant.address || ''}
                                        onChange={(e) => handleParticipantChange(index, e)}
                                        required
                                        disabled={registrationLoading}
                                        placeholder="Straße, Hausnummer, PLZ, Ort"
                                        rows="2"
                                      />
                                    </div>

                                    <div className="form-row">
                                      <div className="form-group">
                                        <label htmlFor={`idCardNumber_${index}`}>Personalausweisnummer *</label>
                                        <input
                                          type="text"
                                          id={`idCardNumber_${index}`}
                                          name="idCardNumber"
                                          value={participant.idCardNumber || ''}
                                          onChange={(e) => handleParticipantChange(index, e)}
                                          required
                                          disabled={registrationLoading}
                                          placeholder="z.B. L01X00T478"
                                        />
                                      </div>

                                      <div className="form-group">
                                        <label htmlFor={`dateOfBirth_${index}`}>Geburtsdatum *</label>
                                        <input
                                          type="date"
                                          id={`dateOfBirth_${index}`}
                                          name="dateOfBirth"
                                          value={participant.dateOfBirth || ''}
                                          onChange={(e) => handleParticipantChange(index, e)}
                                          required
                                          disabled={registrationLoading}
                                        />
                                      </div>
                                    </div>

                                    <div className="form-group">
                                      <label htmlFor={`placeOfBirth_${index}`}>Geburtsort *</label>
                                      <input
                                        type="text"
                                        id={`placeOfBirth_${index}`}
                                        name="placeOfBirth"
                                        value={participant.placeOfBirth || ''}
                                        onChange={(e) => handleParticipantChange(index, e)}
                                        required
                                        disabled={registrationLoading}
                                        placeholder="z.B. Frankfurt am Main"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="form-group">
                          <label htmlFor="message">Nachricht (optional)</label>
                          <textarea
                            id="message"
                            name="message"
                            value={registrationData.message || ''}
                            onChange={handleRegistrationChange}
                            placeholder="Zusätzliche Informationen..."
                            rows="3"
                            disabled={!isEditMode && isRegistered}
                          />
                        </div>

                        <div className="registration-actions">
                          {isEditMode ? (
                            <>
                              <button
                                type="submit"
                                className="btn btn-success"
                                disabled={registrationLoading}
                              >
                                {registrationLoading ? 'Wird gespeichert...' : '💾 Änderungen speichern'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditMode(false);
                                  checkUserRegistration(); // Daten zurücksetzen
                                }}
                                className="btn btn-secondary"
                                disabled={registrationLoading}
                              >
                                Abbrechen
                              </button>
                            </>
                          ) : (
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={registrationLoading}
                            >
                              {registrationLoading
                                ? 'Wird verarbeitet...'
                                : (registrationType === 'single' ? 'Anmelden' : 'Gruppe anmelden')
                              }
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GameDetail;
